import { toValue, type MaybeRefOrGetter } from 'vue';
import { toast } from 'vue-sonner';
import type { Invoice, Quote } from '~/types/models';

/** Shared row actions for commercial documents in client and project workspaces. */
export function useCommercialDocumentActions(options: {
    canMutate: MaybeRefOrGetter<boolean>;
    refresh: () => Promise<unknown>;
}) {
    const invoices = useInvoicesStore();
    const quotes = useQuotesStore();
    const pending = new Set<string>();

    async function run(key: string, action: () => Promise<unknown>, success: string, failure: string) {
        if (!toValue(options.canMutate) || pending.has(key)) return;
        pending.add(key);
        try {
            await action();
            await options.refresh();
            toast.success(success);
        } catch (error: any) {
            toast.error(error?.data?.statusMessage || error?.message || failure);
        } finally {
            pending.delete(key);
        }
    }

    return {
        sendInvoice: (id: string, method?: 'both' | 'portal') => run(`invoice:${id}`, () => method ? invoices.sendInvoice(id, method) : invoices.sendInvoice(id), method === 'portal' ? 'Invoice sent to portal.' : 'Invoice sent.', 'Failed to send invoice.'),
        remindInvoice: (id: string) => run(`invoice:${id}`, () => invoices.sendInvoiceReminder(id), 'Invoice reminder sent.', 'Failed to send invoice reminder.'),
        markInvoicePaid: (id: string) => run(`invoice:${id}`, () => invoices.markInvoicePaid(id), 'Invoice marked paid.', 'Failed to mark invoice paid.'),
        copyInvoiceLink: (invoice: Invoice) => run(`invoice:${invoice.id}`, async () => {
            const link = await invoices.ensureInvoicePublicLink(invoice.id, invoice);
            await navigator.clipboard.writeText(link);
        }, 'Public invoice link copied.', 'Failed to copy invoice link.'),
        sendQuote: (id: string) => run(`quote:${id}`, () => quotes.sendQuote(id), 'Quote sent.', 'Failed to send quote.'),
        remindQuote: (id: string) => run(`quote:${id}`, () => quotes.sendQuoteReminder(id), 'Quote reminder sent.', 'Failed to send quote reminder.'),
        acceptQuote: (id: string) => run(`quote:${id}`, () => quotes.acceptQuote(id), 'Quote accepted.', 'Failed to accept quote.'),
        approveQuoteInternally: (id: string) => run(`quote:${id}`, () => quotes.approveQuoteInternally(id), 'Quote approved internally.', 'Failed to approve quote internally.'),
        rejectQuote: (id: string) => run(`quote:${id}`, () => quotes.rejectQuote(id), 'Quote rejected.', 'Failed to reject quote.'),
        createFullInvoice: (id: string) => run(`quote:${id}`, () => quotes.createFullInvoiceFromQuote(id), 'Full invoice created.', 'Failed to create full invoice.'),
        createDepositInvoice: (id: string) => run(`quote:${id}`, () => quotes.createDepositInvoiceFromQuote(id), 'Deposit invoice created.', 'Failed to create deposit invoice.'),
        createBalanceInvoice: (id: string) => run(`quote:${id}`, () => quotes.createBalanceInvoiceFromQuote(id), 'Balance invoice created.', 'Failed to create balance invoice.'),
        createNextInstallmentInvoice: (id: string) => run(`quote:${id}`, () => quotes.createNextInstallmentInvoiceFromQuote(id), 'Next installment created.', 'Failed to create installment invoice.'),
        copyQuoteLink: (quote: Quote) => run(`quote:${quote.id}`, async () => {
            const link = await quotes.ensureQuotePublicLink(quote.id, quote);
            await navigator.clipboard.writeText(link);
        }, 'Public quote link copied.', 'Failed to copy quote link.'),
    };
}
